// ESTUDIO FOTOVOLTAICO DE FLUXAI — la mitad de acá del espejo entrante.
//
// Decisión del dueño (2026-08-09): el estudio de alto nivel, el que se arma sobre la TELEMETRÍA
// real del medidor, **se queda en FluxAI**. Solar Hub lo LEE; no adivina esas cifras ni se conecta
// a la base de medidores. FluxAI cerró su mitad el 2026-08-11 (`internal/suite/estudio`).
//
// Segundo consumidor del espejo (`services/flux.js`), calcado de `pliego.js`: no sabe de tokens,
// ni de por cuál escalón salió — pide `estudio` y listo.
//
// 🔑 REGLA: leer, nunca copiar. El estudio tiene UN dueño (FluxAI) y acá se muestra en vivo. Guardar
// una copia local traería el modo de falla que este diseño evita: dos versiones y nadie sabe cuál
// está al día. Por eso la caché es CORTA y con TTL, no persistencia.
//
// ⚠️ FluxAI responde **200 + `encontrado:false`** cuando el estudio no existe — NUNCA 404. No es un
// descuido suyo: `services/flux.js` lee 404/503 como canal caído y apagaría ese escalón de la
// cascada para toda la sesión. Acá eso llega como `r` con
// `encontrado:false`, o sea una RESPUESTA, y se cachea como `null` igual que el pliego.

import { pedirAFlux, reiniciarCanalFlux } from './flux';

// Un estudio guardado solo cambia si su autor lo vuelve a guardar. 5 min evita repetir el viaje al
// navegar entre pestañas sin arriesgarse a mostrar una versión vieja después de una edición.
const cache = new Map();
const TTL_MS = 5 * 60 * 1000;

/** Handle opaco que acuña FluxAI: `fx:` + UUID v4. Se valida acá para no gastar el viaje. */
const RE_HANDLE = /^fx:[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;

/** `true` si el texto tiene forma de handle de estudio de FluxAI. */
export function esHandleFlux(handle) {
  return typeof handle === 'string' && RE_HANDLE.test(handle.trim());
}

/**
 * Trae un estudio de FluxAI por su handle. Devuelve `null` cuando no hay dato utilizable — el
 * llamador NO tiene que distinguir "falló el canal" de "ese estudio no existe": en los dos casos
 * no hay nada que mostrar, y la UI lo dice con el mismo texto.
 *
 * @returns {Promise<null | {handle, contrato, emitido, actualizado, alcanceTipo, sistema,
 *                           emplazamiento, simulacion, consumo, economico, techo}>}
 */
export async function obtenerEstudioFlux(handle) {
  const h = typeof handle === 'string' ? handle.trim() : '';
  if (!esHandleFlux(h)) return null;

  const hit = cache.get(h);
  if (hit && Date.now() - hit.at < TTL_MS) return hit.valor;

  // Sin respaldo a propósito: no hay otra puerta que sepa leer estudios de FluxAI, y
  // pasarle un nombre inventado solo agregaría una llamada perdida por cada consulta.
  const r = await pedirAFlux('estudio', { estudio_id: h });

  let valor = null;
  if (r && r.encontrado && r.estudio) {
    const e = r.estudio;
    valor = {
      handle: e.estudio_id || h,
      contrato: r.contract || null,
      // Las FECHAS son parte del dato: permiten decir de cuándo es el estudio en vez de
      // presentarlo como si se hubiera calculado recién.
      emitido: e.emitido || null,
      actualizado: e.actualizado || null,
      alcanceTipo: e.alcance_tipo || null,
      sistema: e.sistema || null,
      emplazamiento: e.emplazamiento || null,
      simulacion: e.simulacion || null,
      // Trae `perfil_horario_kwh` CON su procedencia (`periodo.dias_datos` / `cobertura_pct`):
      // sin ese par no se distingue un perfil de 12 días de uno de 365, y esa distinción es
      // justamente lo que separa este estudio de una estimación.
      consumo: e.consumo || null,
      economico: e.economico || null,
      techo: e.techo || null,
      // 📄 LA DOCUMENTACIÓN DEL ESTUDIO — memoria técnica RETIE renderizada en FluxAI.
      //
      // Este mapeo es una LISTA BLANCA, igual que `proyectarEstudio` del lado de FluxAI: un campo
      // que no se nombre acá se cae en silencio, sin error y sin rastro. Por eso el documento
      // tiene que declararse en los DOS extremos o el estudio llega mudo aunque el servidor lo
      // haya mandado completo.
      //
      // Llega el HTML ya renderizado a propósito: el generador de memoria de Solar Hub queda
      // PARALIZADO por decisión del dueño (2026-08-09) y el estudio de pago vive en FluxAI. Dos
      // generadores del mismo documento firmable terminan discrepando, y el que se radica ante el
      // operador de red no puede ser el que quedó viejo.
      documentos: e.documentos || null,
    };
  }

  cache.set(h, { at: Date.now(), valor });
  return valor;
}

/** Vacía la caché (útil en pruebas y al cambiar de sesión). */
export function limpiarCacheEstudioFlux() {
  cache.clear();
  // También el recuerdo de "el canal directo no está": si no, una prueba que lo apaga contamina a
  // las siguientes y el estado queda pegado entre sesiones del cliente.
  reiniciarCanalFlux();
}
